import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DataInput } from '@/components/DataInput/DataInput'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { EditorWrapper } from '@/features/Templates/components/EditorWrapper'
import { templateCreateRoute } from '@/features/Templates/components/TemplateCreate'
import { templateTableRoute } from '@/features/Templates/page/TemplateTable'
import {
  useArchiveTemplate,
  useGetTemplateById,
} from '@/generated/http-clients/v3ApiComponents'
import { toaster } from '@/hooks/useToast/toastUtils'
import { templatesRootRoute } from '@/router/rootRoutes'
import {
  Badge,
  Button,
  Field,
  HStack,
  Skeleton,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import get from 'lodash/get'
import { Archive, ArchiveRestore } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const templateDetailsRoute = createRoute({
  getParentRoute: () => templatesRootRoute,
  path: '$templateId',
  component: () => <TemplateDetails />,
})

export function TemplateDetails() {
  const { t } = useTranslation('features', { useSuspense: false })
  const { t: tGeneral } = useTranslation()
  const navigate = useNavigate()

  const params = templateDetailsRoute.useParams()
  const templateId = params.templateId

  if (!templateId) {
    return null
  }

  const template = useGetTemplateById({
    pathParams: {
      templateId,
    },
  })

  const title = `${t('templates.details.titlePrefix')}${templateId}`
  const breadCrumbs = [
    { name: tGeneral('sidebar.templates'), path: templateTableRoute.to },
    { name: title, path: '' },
  ]

  return (
    <PageLayout
      title={
        <HStack alignItems="center" gap={2}>
          {title}
          {template.data?.archived && (
            <Badge size="lg" colorPalette="gray" variant="outline">
              {t('templates.archivedBadge', { ns: 'features' })}
            </Badge>
          )}
        </HStack>
      }
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={<div />}
    >
      {template.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {template.isError ? (
        <ApiErrorState
          showApiErrorContext
          apiResource={template as unknown as UseQueryResult}
          errorTitle={t('templates.details.errorTitle')}
          errorDescription={t('templates.details.errorDescription')}
        />
      ) : null}
      {template.isSuccess ? (
        <>
          <SectionCard
            title={t('templates.details.detailsSectionTitle') ?? undefined}
            actions={
              <HStack gap={2}>
                <TemplateArchiveButton
                  templateId={templateId}
                  isArchived={template.data?.archived}
                  onSuccess={template.refetch}
                />
                <Button
                  variant="solid"
                  onClick={() =>
                    navigate({
                      to: templateCreateRoute.to,
                      search: {
                        templateId,
                      },
                    })
                  }
                >
                  {t('templates.details.duplicateAndEditButton')}
                </Button>
              </HStack>
            }
          >
            <VStack alignItems="start">
              <Field.Root>
                <Field.Label fontWeight={600} fontSize={18}>
                  {t('templates.details.generalLabel')}
                </Field.Label>
              </Field.Root>
              <DataInput
                label={t('templates.details.nameLabel')}
                value={template.data?.name}
              />
              <DataInput
                label={t('templates.details.descriptionLabel')}
                value={template.data?.description}
              />
              <Field.Root>
                <Field.Label mt={4} fontWeight={600} fontSize={18}>
                  {t('templates.details.chartInfoLabel')}
                </Field.Label>
              </Field.Root>
              <DataInput
                label={t('templates.details.repositoryUrlLabel')}
                value={template.data?.chart_source.chart_repo_url}
              />
              <DataInput
                mt={1}
                label={t('templates.details.targetRevisionLabel')}
                value={template.data?.chart_source.chart_target_revision}
              />
              <DataInput
                mt={1}
                label={t('templates.details.chartPathLabel')}
                value={template.data?.chart_source.chart_path}
              />
            </VStack>
          </SectionCard>
          <SectionCard
            title={t('templates.details.yamlSectionTitle') ?? undefined}
          >
            {/*  @ts-ignore template yaml is always there or empty */}
            <EditorWrapper readOnly={true} yamlContent={template.data?.yaml} />
          </SectionCard>
        </>
      ) : null}
    </PageLayout>
  )
}

export function TemplateArchiveButton({
  templateId,
  isArchived,
  onSuccess,
}: Readonly<{
  templateId: string
  isArchived?: boolean
  onSuccess?: () => void
}>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { hasAdminRights } = useRBACPermissions()
  const isAdmin = hasAdminRights()

  const archiveTemplate = useArchiveTemplate({
    onSuccess: () => {
      toaster.create({
        title: isArchived
          ? t('templates.details.reactivateSuccess')
          : t('templates.details.archiveSuccess'),
        type: 'success',
      })
      onSuccess?.()
    },
    onError: (error) => {
      toaster.create({
        title: t('templates.details.archiveError'),
        description: get(error, 'message'),
        type: 'error',
      })
    },
  })

  const handleArchiveToggle = () => {
    archiveTemplate.mutate({
      pathParams: { templateId },
      body: { archived: !isArchived },
    })
  }

  // Only display archive button for admin users
  if (!isAdmin) {
    return null
  }

  return (
    <Button
      variant="outline"
      onClick={handleArchiveToggle}
      loading={archiveTemplate.isPending}
      disabled={archiveTemplate.isPending}
    >
      {isArchived ? (
        <>
          <ArchiveRestore />
          {t('templates.details.reactivateButton', {
            ns: 'features',
          })}
        </>
      ) : (
        <>
          <Archive />
          {t('templates.details.archiveButton')}
        </>
      )}
    </Button>
  )
}
